import React, { useState, useEffect, useRef } from 'react';
import { TermsAndConditionsContent, PrivacyPolicyContent } from './LegalContent';

const LegalPage: React.FC = () => {
    const [activeSection, setActiveSection] = useState('terms');
    const sectionsRef = useRef<Record<string, HTMLElement | null>>({});

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setActiveSection(entry.target.id);
                    }
                });
            },
            { rootMargin: '-20% 0px -80% 0px', threshold: 0 }
        );

        const currentSections = sectionsRef.current;
        Object.values(currentSections).forEach((section) => {
            // FIX: Use instanceof HTMLElement as a type guard to ensure `section` is an Element.
            if (section instanceof HTMLElement) {
                observer.observe(section);
            }
        });

        return () => {
            Object.values(currentSections).forEach((section) => {
                // FIX: Use instanceof HTMLElement as a type guard to ensure `section` is an Element.
                if (section instanceof HTMLElement) {
                    observer.unobserve(section);
                }
            });
        };
    }, []);

    const scrollToSection = (id: string) => {
        sectionsRef.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    
    const NavLink: React.FC<{ id: string; label: string }> = ({ id, label }) => (
        <button
            onClick={() => scrollToSection(id)}
            className={`w-full text-left p-3 rounded-md font-semibold transition-colors ${
                activeSection === id
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-100'
            }`}
        >
            {label}
        </button>
    );

    return (
        <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
             <div className="text-center mb-12">
                <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-gray-900">Legal & Policies Center</h1>
                <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto">
                    Our commitment to transparency and your rights.
                </p>
            </div>
            <div className="flex flex-col md:flex-row gap-12">
                {/* Left Sidebar */}
                <aside className="md:w-1/4">
                    <div className="sticky top-24 space-y-2">
                        <h2 className="text-lg font-bold text-gray-800 mb-4 px-3">Navigation</h2>
                        <NavLink id="terms" label="Terms of Service" />
                        <NavLink id="privacy" label="Privacy Policy" />
                    </div>
                </aside>

                {/* Right Content */}
                <main className="md:w-3/4 space-y-16">
                    {/* FIX: The ref callback should not return a value. Wrapped in braces to prevent implicit return. */}
                    <section id="terms" ref={(el) => { sectionsRef.current['terms'] = el; }} className="scroll-mt-24">
                        <TermsAndConditionsContent />
                    </section>
                    {/* FIX: The ref callback should not return a value. Wrapped in braces to prevent implicit return. */}
                    <section id="privacy" ref={(el) => { sectionsRef.current['privacy'] = el; }} className="scroll-mt-24">
                        <PrivacyPolicyContent />
                    </section>
                </main>
            </div>
        </div>
    );
};

export default LegalPage;